import React from 'react';
import { View, Text, Pressable, StyleSheet, Platform } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { addColorOpacity } from '../../shared/utils/styles';
import { BasePropertyType, PROPERTY_TYPE_CONFIG } from '../../shared/types/due-diligence';
import { Ionicons } from '@expo/vector-icons';

export interface PropertyTypeSelectorProps {
  value: BasePropertyType;
  onSelect: (type: BasePropertyType) => void;
  disabled?: boolean;
}

export function PropertyTypeSelector({ 
  value, 
  onSelect,
  disabled = false
}: PropertyTypeSelectorProps) {
  const styles = useStyles();
  const { tokens, utils } = styles;
  const { isMobile, isSmallMobile } = useResponsive();
  const selectorStyles = makeSelectorStyles(tokens, utils, isMobile, isSmallMobile);

  const propertyTypes: BasePropertyType[] = [
    'raw_land',
    'improved_land',
    'residential',
    'agricultural',
    'timber',
    'recreational',
    'mixed_use',
  ];

  return (
    <View style={selectorStyles.container}>
      <Text style={selectorStyles.label}>Property Type</Text>
      <View style={selectorStyles.grid}>
        {propertyTypes.map((type) => {
          const config = PROPERTY_TYPE_CONFIG[type];
          const isSelected = type === value;

          return (
            <Pressable
              key={type}
              onPress={() => !disabled && onSelect(type)}
              disabled={disabled}
              style={({ pressed }) => [
                selectorStyles.option,
                isSelected && selectorStyles.optionSelected,
                (pressed || disabled) && selectorStyles.optionPressed,
                disabled && selectorStyles.optionDisabled,
              ]}
            >
              <View style={selectorStyles.optionContent}>
                <View style={[
                  selectorStyles.iconContainer,
                  isSelected && selectorStyles.iconContainerSelected,
                ]}>
                  <Ionicons
                    name="home-outline" as any
                    size={isMobile ? 20 : 24}
                    color={isSelected ? tokens.color.primary : tokens.color.onSurface}
                  />
                </View>
                <Text style={[
                  selectorStyles.optionLabel,
                  isSelected && selectorStyles.optionLabelSelected,
                ]}>
                  {config.label}
                </Text>
                {config.description && !isSmallMobile && (
                  <Text style={selectorStyles.optionDescription}>
                    {config.description}
                  </Text>
                )}
              </View>
              {isSelected && (
                <View style={selectorStyles.checkmark}>
                  <Ionicons
                    name="checkmark-circle"
                    size={20}
                    color={tokens.color.primary}
                  />
                </View>
              )}
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}

function makeSelectorStyles(
  tokens: ReturnType<typeof useStyles>['tokens'],
  utils: ReturnType<typeof useStyles>['utils'],
  isMobile: boolean,
  isSmallMobile: boolean
) {
  return StyleSheet.create({
    container: {
      width: '100%',
    },
    label: {
      fontSize: utils.typography.sizes.base,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onBackground,
      marginBottom: utils.spacing.md,
      fontFamily: utils.typography.body.fontFamily,
    },
    grid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: utils.spacing.md,
    },
    option: {
      flex: isMobile ? (isSmallMobile ? 1 : 1) : 0,
      minWidth: isMobile ? (isSmallMobile ? '48%' : '48%') : 180,
      maxWidth: isMobile ? (isSmallMobile ? '48%' : '48%') : 220,
      padding: utils.spacing.md,
      borderRadius: utils.radius.md,
      backgroundColor: tokens.color.surface,
      borderWidth: 2,
      borderColor: tokens.color.border,
      ...Platform.select({
        web: {
          cursor: 'pointer',
          transition: 'all 0.15s ease',
        } as any,
      }),
    },
    optionSelected: {
      borderColor: tokens.color.primary,
      backgroundColor: addColorOpacity(tokens.color.primary, 0.05),
    },
    optionPressed: {
      opacity: 0.7,
    },
    optionDisabled: {
      opacity: 0.5,
    },
    optionContent: {
      alignItems: 'center',
      gap: utils.spacing.xs,
    },
    iconContainer: {
      width: isMobile ? 40 : 48,
      height: isMobile ? 40 : 48,
      borderRadius: utils.radius.full,
      backgroundColor: addColorOpacity(tokens.color.onSurface, 0.1),
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: utils.spacing.xs,
    },
    iconContainerSelected: {
      backgroundColor: addColorOpacity(tokens.color.primary, 0.15),
    },
    optionLabel: {
      fontSize: isSmallMobile ? utils.typography.sizes.sm : utils.typography.sizes.base,
      fontWeight: utils.typography.weights.medium,
      color: tokens.color.onSurface,
      textAlign: 'center',
      fontFamily: utils.typography.body.fontFamily,
    },
    optionLabelSelected: {
      color: tokens.color.primary,
      fontWeight: utils.typography.weights.semibold,
    },
    optionDescription: {
      fontSize: utils.typography.sizes.xs,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      textAlign: 'center',
      fontFamily: utils.typography.body.fontFamily,
      marginTop: 2,
    },
    checkmark: {
      position: 'absolute',
      top: utils.spacing.xs,
      right: utils.spacing.xs,
    },
  });
}

